import { Helmet } from 'react-helmet-async'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { db } from '../lib/supabase'
import LoadingSpinner from '../components/LoadingSpinner'

export default function Marcas() {
  const [searchParams] = useSearchParams()
  const [marcas, setMarcas] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlyDiscounts, setShowOnlyDiscounts] = useState(false)
  const [loading, setLoading] = useState(true)
  const [heroConfig, setHeroConfig] = useState(null)
  const [seoConfig, setSeoConfig] = useState(null)
  const [faqs, setFaqs] = useState([])

  useEffect(() => {
    loadData()
    loadHeroConfig()
    loadSEOConfig()
    loadFAQs()
  }, [])

  // Check URL params for discount filter and search query
  useEffect(() => {
    const hasDescuentosParam = searchParams.get('descuentos') === 'true'
    setShowOnlyDiscounts(hasDescuentosParam)
    
    const searchQuery = searchParams.get('q')
    if (searchQuery) {
      setSearchTerm(searchQuery)
    }
  }, [searchParams])

  const loadData = async () => {
    const { data: marcasData } = await db.getTiendas()
    
    if (marcasData) setMarcas(marcasData)
    setLoading(false)
  }

  const loadHeroConfig = async () => {
    const { data } = await db.getConfiguracionPortada('tiendas')
    setHeroConfig(data || null)
  }

  const loadSEOConfig = async () => {
    const { data } = await db.getSEOConfig('marcas')
    setSeoConfig(data || null)
  }

  const loadFAQs = async () => {
    const { data } = await db.getFAQsByScope('page', null, 'marcas')
    setFaqs(data || [])
  }

  const filteredMarcas = marcas.filter((marca) => {
    const matchesSearch = !searchTerm || 
      marca.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    
    const hasDiscount = marca.descuentos && marca.descuentos[0]?.count > 0
    const matchesDiscount = !showOnlyDiscounts || hasDiscount
    
    return matchesSearch && matchesDiscount
  })

  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Tiendas - Plaza Aurora Outlet'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Descubre todas las marcas disponibles en Plaza Aurora Outlet'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/marcas`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/marcas`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Tiendas - Plaza Aurora Outlet'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Descubre todas las marcas disponibles en Plaza Aurora Outlet'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Tiendas - Plaza Aurora Outlet'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Descubre todas las marcas disponibles en Plaza Aurora Outlet'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
        
        {/* LLM Discovery - JSON Feeds */}
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/api/brands.json`} title="Brands List" />
        <link rel="alternate" type="application/json" href={`${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/api/categories.json`} title="Categories List" />
      </Helmet>
      
      {/* Breadcrumb JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Tiendas",
              "item": `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/marcas`
            }
          ]
        })}
      </script>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}

      {/* Main Content */}
      <main>
        <div className="container" style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          {/* Hero Section */}
          <section id="hero_main" role="region" aria-label="Hero Tiendas" style={{ marginTop: '1.5rem' }}>
            <div className="heroContainer" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 0.9fr',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '1.25rem',
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden'
            }}>
              <div className="heroLeft" style={{ padding: '1rem' }}>
                <h1 className="heroTitle" style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 'var(--font-weight-bold)',
                  lineHeight: '0.95',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 1rem 0'
                }}>
                  {heroConfig?.hero_titulo || 'OUTLETS CON DESCUENTOS TODO EL AÑO'}<br/>
                </h1>
                <p className="heroDescription" style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lead)',
                  color: 'var(--color-text-muted)',
                  maxWidth: '48ch',
                  lineHeight: 'var(--line-height-relaxed)',
                  marginBottom: '1.5rem'
                }}>
                  {heroConfig?.hero_descripcion || 'Las mejores tiendas a precios outlet. Calidad y estilo sin sacrificar tu presupuesto 🛍️'}
                </p>
                <div className="heroActions" style={{ display: 'flex', gap: '0.75rem' }}>
                  <Link 
                    to={heroConfig?.hero_boton_1_url || '/marcas'} 
                    className={heroConfig?.hero_boton_1_estilo === 'outline' ? 'btnOutline' : 'btnPrimary'} 
                    style={{
                      padding: '0.6rem 1.1rem',
                      background: 'var(--color-cta-bg)',
                      color: 'var(--color-cta-text)',
                      borderRadius: 'var(--radius-sm)',
                      fontWeight: 'var(--font-weight-bold)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                      fontSize: 'var(--font-size-body)',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                      e.currentTarget.style.background = 'var(--color-cta-bg)'
                      e.currentTarget.style.color = 'var(--color-cta-text)'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                      e.currentTarget.style.background = 'var(--brand-blue-600)'
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                      e.currentTarget.style.background = 'var(--brand-blue)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onTouchEnd={(e) => {
                      setTimeout(() => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.background = 'var(--color-cta-bg)'
                        e.currentTarget.style.color = 'var(--color-cta-text)'
                      }, 150)
                    }}
                  >
                    {heroConfig?.hero_boton_1_texto || 'Ver Tiendas'}
                  </Link>
                  <Link to={heroConfig?.hero_boton_2_url || '/ubicanos'} className={heroConfig?.hero_boton_2_estilo === 'primary' ? 'btnPrimary' : 'btnOutline'} style={{
                    padding: '0.6rem 1.1rem',
                    background: 'transparent',
                    border: '1px solid var(--color-stroke)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-primary)',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}>
                    {heroConfig?.hero_boton_2_texto || 'Ubícanos'}
                  </Link>
                </div>
              </div>
              <div className="heroRight" style={{ height: '100%' }}>
                {heroConfig?.hero_imagen_url ? (
                  <img 
                    className="heroImage" 
                    src={heroConfig.hero_imagen_url} 
                    alt={heroConfig?.hero_imagen_alt || 'Nuestras Tiendas'} 
                    style={{
                      width: '100%',
                      height: '420px',
                      objectFit: 'cover',
                      borderRadius: 'var(--radius-md)'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '420px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-accent-gray)'
                    }}
                  />
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Promo Strip - Full Width */}
        <section id="promo_strip" role="complementary" aria-label="Promo marquee" style={{ 
          marginTop: '1.5rem', 
          overflow: 'hidden', 
          display: heroConfig?.marquee_activo === false ? 'none' : 'block'
        }}>
          {heroConfig?.marquee_link_url
            ? (heroConfig.marquee_link_url.startsWith('/')
                ? (
                  <Link to={heroConfig.marquee_link_url} style={{ textDecoration: 'none', cursor: 'pointer' }}>
                    <div className="marquee" style={{
                      background: 'var(--color-black-block)',
                      color: 'var(--color-cta-text)',
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s ease'
                    }}>
                      <div className="marquee-content" style={{
                        display: 'inline-block',
                        animation: 'scroll-left 25s linear infinite',
                        paddingLeft: '100%'
                      }}>
                        {heroConfig?.marquee_texto || '★ CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS  •  PRECIOS OUTLET  •  MODA Y ESTILO  •  CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS  •  PRECIOS OUTLET  •  MODA Y ESTILO  •  CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS'}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <a href={heroConfig.marquee_link_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', cursor: 'pointer' }}>
                    <div className="marquee" style={{
                      background: 'var(--color-black-block)',
                      color: 'var(--color-cta-text)',
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.875rem',
                      padding: '0.5rem 0',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s ease'
                    }}>
                      <div className="marquee-content" style={{
                        display: 'inline-block',
                        animation: 'scroll-left 25s linear infinite',
                        paddingLeft: '100%'
                      }}>
                        {heroConfig?.marquee_texto || '★ CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS  •  PRECIOS OUTLET  •  MODA Y ESTILO  •  CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS  •  PRECIOS OUTLET  •  MODA Y ESTILO  •  CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS'}
                      </div>
                    </div>
                  </a>
                )
              )
            : (
              <div className="marquee" style={{
                background: 'var(--color-black-block)',
                color: 'var(--color-cta-text)',
                fontFamily: 'var(--font-display)',
                fontSize: '0.875rem',
                padding: '0.5rem 0',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }}>
                <div className="marquee-content" style={{
                  display: 'inline-block',
                  animation: 'scroll-left 25s linear infinite',
                  paddingLeft: '100%'
                }}>
                  {heroConfig?.marquee_texto || '★ CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS  •  PRECIOS OUTLET  •  MODA Y ESTILO  •  CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS  •  PRECIOS OUTLET  •  MODA Y ESTILO  •  CALIDAD GARANTIZADA  •  MARCAS RECONOCIDAS'}
                </div>
              </div>
            )
          }
        </section>

        <div className="container" style={{ maxWidth: 'var(--container-max-width)', margin: '0 auto', padding: '0 2rem' }}>
        {/* Brands Section */}
        <section id="brands_main" role="region" aria-label="Nuestras Tiendas">
          {/* Header */}
          <div style={{ marginBottom: '2.5rem', marginTop: '1.5rem' }}>
            <h2 style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}>Nuestras Tiendas</h2>
          </div>

          {/* Search Bar and Filters */}
          <div style={{ 
            marginBottom: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              placeholder="Buscar tiendas"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: '1',
                minWidth: '300px',
                maxWidth: '500px',
                padding: '0.75rem 1.25rem',
                border: '1px solid var(--color-stroke)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-body)',
                color: 'var(--color-text-primary)',
                background: 'var(--color-background)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = 'none'
                e.currentTarget.style.borderColor = 'var(--brand-blue)'
                e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-stroke)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
            
            {/* Filtro de Descuentos */}
            <button
              onClick={() => setShowOnlyDiscounts(!showOnlyDiscounts)}
              style={{
                padding: '0.75rem 1.5rem',
                background: showOnlyDiscounts 
                  ? 'linear-gradient(135deg, #0ACC66 0%, #089B4D 100%)'
                  : 'white',
                color: showOnlyDiscounts ? 'white' : 'var(--color-text-primary)',
                border: showOnlyDiscounts 
                  ? 'none'
                  : '1px solid var(--color-stroke)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-body)',
                fontWeight: showOnlyDiscounts ? 'var(--font-weight-bold)' : 'var(--font-weight-medium)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: showOnlyDiscounts ? '0 2px 8px rgba(10, 204, 102, 0.3)' : 'none',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (!showOnlyDiscounts) {
                  e.currentTarget.style.background = 'var(--brand-green-50)'
                  e.currentTarget.style.borderColor = 'var(--brand-green-300)'
                }
              }}
              onMouseLeave={(e) => {
                if (!showOnlyDiscounts) {
                  e.currentTarget.style.background = 'white'
                  e.currentTarget.style.borderColor = 'var(--color-stroke)'
                }
              }}
              onMouseDown={(e) => {
                if (!showOnlyDiscounts) {
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-green-200)'
                }
              }}
              onTouchStart={(e) => {
                if (!showOnlyDiscounts) {
                  e.currentTarget.style.background = 'var(--brand-green-50)'
                  e.currentTarget.style.borderColor = 'var(--brand-green-300)'
                }
                e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-green-200)'
              }}
              onTouchEnd={(e) => {
                setTimeout(() => {
                  e.currentTarget.style.boxShadow = showOnlyDiscounts ? '0 2px 8px rgba(10, 204, 102, 0.3)' : 'none'
                  if (!showOnlyDiscounts) {
                    e.currentTarget.style.background = 'white'
                    e.currentTarget.style.borderColor = 'var(--color-stroke)'
                  }
                }, 150)
              }}
            >
              🏷️ {showOnlyDiscounts ? 'Con Descuento' : 'Solo Descuentos'}
            </button>
          </div>

          {/* Brands Grid */}
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem'
            }}>
              <LoadingSpinner message="" size={64} />
            </div>
          ) : filteredMarcas.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-md)'
            }}>
              <p style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-body)',
                color: 'var(--color-text-muted)'
              }}>No se encontraron tiendas</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '1rem',
              marginBottom: '3rem'
            }}>
              {filteredMarcas.map((marca) => (
                <Link
                  key={marca.id}
                  to={`/marcas/${marca.id}`}
                  style={{
                    background: 'white',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: 'none',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'block',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = 'var(--shadow-card)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = 'scale(0.98)'
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  }}
                  onTouchEnd={(e) => {
                    setTimeout(() => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                    }, 150)
                  }}
                >
                  {/* Badge de Descuento */}
                  {marca.descuentos && marca.descuentos[0]?.count > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '0.75rem',
                      right: '0.75rem',
                      padding: '0.375rem 0.75rem',
                      background: 'linear-gradient(135deg, #F79431 0%, #FF6B6B 100%)',
                      color: 'white',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-small)',
                      fontWeight: 'var(--font-weight-bold)',
                      boxShadow: '0 2px 8px rgba(247, 148, 49, 0.3)',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      🏷️ DESCUENTO
                    </div>
                  )}
                  {/* Brand Logo */}
                  <div style={{
                    aspectRatio: '1',
                    background: 'var(--color-accent-gray)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 rem'
                  }}>
                    {marca.logo_url ? (
                      <img
                        src={marca.logo_url}
                        alt={marca.nombre}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <p style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 'var(--font-weight-bold)',
                        fontSize: 'var(--font-size-h3)',
                        color: 'var(--color-text-primary)',
                        textAlign: 'center',
                        margin: 0
                      }}>
                        {marca.nombre}
                      </p>
                    )}
                  </div>

                  {/* Brand Info */}
                  <div style={{ padding: '1rem' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--color-text-primary)',
                      margin: '0 0 0.25rem 0'
                    }}>
                      {marca.nombre}
                    </h3>
                    {marca.descripcion && (
                      <p style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 'var(--small)',
                        color: 'var(--textMuted)',
                        margin: '0 0 0.75rem 0',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {marca.descripcion}
                      </p>
                    )}
                    {marca.puntuacion_promedio > 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1rem' }}>⭐</span>
                        <span style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 'var(--small)',
                          fontWeight: 'var(--medium)',
                          color: 'var(--textPrimary)'
                        }}>
                          {marca.puntuacion_promedio.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* FAQs Section */}
        {faqs.length > 0 && (
          <section id="faqs" role="region" aria-label="Preguntas Frecuentes" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-display-l)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              textAlign: 'center',
              marginBottom: '2rem'
            }}>PREGUNTAS FRECUENTES</h2>
            
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {faqs.map((faq) => (
                <details key={faq.id} style={{
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  border: '1px solid var(--color-stroke)',
                  cursor: 'pointer'
                }}>
                  <summary style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-h4)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-text-primary)',
                    listStyle: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    {faq.pregunta}
                    <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                  </summary>
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-muted)',
                    lineHeight: '1.6',
                    marginTop: '1rem',
                    whiteSpace: 'pre-line'
                  }}>
                    {faq.respuesta}
                  </p>
                </details>
              ))}
            </div>
            
            {/* FAQPage JSON-LD */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map(faq => ({
                  "@type": "Question",
                  "name": faq.pregunta,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.respuesta
                  }
                }))
              })}
            </script>
          </section>
        )}
        </div>
      </main>

      <style>{`
        @media (min-width: 600px) and (max-width: 1023px) {
          .container {
            padding: 0 1.5rem !important;
          }
          .container > section > div:last-child {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 599px) {
          .container {
            padding: 0 1rem !important;
            padding-top: 0rem !important;
          }
          .container > section > div:last-child {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  )
}



